import React, { useState, useEffect, useRef } from 'react'
import { GlobalContext } from './GlobalContext'
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom"

// utilities
import CloudformationOutputs from './CloudformationOutputs.json'
import { checkIfSubscriptionExpiringWithoutPayment } from './utils/checkIfSubscriptionExpiringWithoutPayment'
import { publish } from './utils/pubsub'

import { v4 as uuidv4 } from 'uuid'
// UI Elements
import Footer from './ui-elements/Footer'
import Header from './ui-elements/Header'

// shared components
import AppTermsDialog from './shared-components/AppTermsDialog'
import MessageModal from './ui-elements/MessageModal'
import Spinner from './ui-elements/Spinner'
import NotLoggedIn from './auth/NotLoggedIn'
import Dashboard from './dashboard/Dashboard'
import NoOrganisation from './ui-elements/NoOrganisation'
import ReconnectModal from './ui-elements/ReconnectModal'
import LoginModal from './ui-elements/LoginModal'
import ListUsers from './dashboard/ListUsers'
import AutomaticReportsForm from './automatic-reports/AutomaticReportsForm'
import AutomaticReportsList from './automatic-reports/AutomaticReportsList'



function App() {

  const [loggedIn, setLoggedIn] = useState<boolean | string>(false)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const [connectionState, setConnectionState] = useState('disconnected')
  const [orgSettings, setOrgSettings] = useState('disconnected')
  const [userData, setUserData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [currentPortalId, setCurrentPortalId] = useState<string | null>(null)
  const WebSocketURI = CloudformationOutputs.WebSocketURI
  const [tableData, setTableData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [loggedInUserRole, setLoggedInUserRole] = useState<string | null>(null)
  const tableDataRef = useRef<any>(null)
  const [activeFeatures, setActiveFeatures] = useState<{ [key: string]: any }>({})
  const [expiredFeatures, setExpiredFeatures] = useState<{ [key: string]: any }>({})
  const [agreementNeeded, setAgreementNeeded] = useState<{ [key: string]: any } | null>(null)
  const [subscriptionHasBeenChecked, setSubscriptionHasBeenChecked] = useState<boolean>(false)
  const [subscriptionExpired, setSubscriptionExpired] = useState<boolean>(false)
  const [subscriptionExpiringWithoutPayment, setSubscriptionExpiringWithoutPayment] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<ObjectStringKeyAnyValue | null>(null)
  const [showReconnectModal, setShowReconnectModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const socketRef = useRef<WebSocket | null>(null)

  // const id = uuidv4()
  // console.log(id)
  // console.log(tableData)

  useEffect(() => {
    // set the socket ref to the current socket
    if (socket) {
      socketRef.current = socket
    } else {
      socketRef.current = null
    }
  }, [socket])

  useEffect(() => {
    checkForToken()
    setConnectionState('tryToConnect')
  }, [])

  useEffect(() => {
    switch (connectionState) {
      case 'closed':
      case 'error':
        setShowReconnectModal(true)
        setConnectionState('waitThenReconnect')
        setTimeout(() => {
          setConnectionState('tryToConnect')
        }, 200)
        break;
      case 'tryToConnect':
        setShowReconnectModal(true)
        setConnectionState('connecting')
        if (!socket) {
          const newSocket = new WebSocket(WebSocketURI)
          //console.log("🔌  opening websocket...")
          newSocket.onopen = () => {
            setSocket(newSocket)
            //console.log("🔌  socket has been opened")
          }
          newSocket.onclose = () => {
            //console.log("🔌  socket closed")
            setConnectionState('closed')
            setSocket(null)
          }
          newSocket.onerror = () => {
            setConnectionState('error')
            //console.log("🔌 socket error")
            setSocket(null)
          }
        } else {
          //console.log("🔌  socket already exists, not creating a new one")
        }
        break

      case 'connecting':
        if (socket) {
          setConnectionState('connected-awaiting-login')
        }
        break

      case 'connected-awaiting-login':
        setShowReconnectModal(true)
        //console.log("🔌  connected and awaiting login")
        //console.log(socket ? 'socket exists' : 'socket does not exist')
        break

      case 'connected-and-logged-in':
        setShowReconnectModal(false)
        //console.log("🔌  connected and logged in")
        break

    }

  }, [connectionState, socket])

  useEffect(() => {
    if (tableData) {
      checkIfSubscriptionExpiringWithoutPayment(tableData, subscriptionHasBeenChecked, setSubscriptionExpiringWithoutPayment, setSubscriptionHasBeenChecked)

      // set logged in user role
      tableDataRef.current = tableData
      if (tableData.Users && userData) {
        const loggedInUserId = userData['cognito:username']
        const role = tableData.Users[loggedInUserId] ? tableData.Users[loggedInUserId].Role : null
        setLoggedInUserRole(role)
      }
    }

  }, [tableData, userData])


  useEffect(() => {
    // handle incoming messages
    if (socket !== null) {
      actOnMessage()
    }
  }, [socket])


  useEffect(() => {
    handleTokens()
  }, [idToken, accessToken, connectionState, loggedIn, currentPortalId, refreshToken])


  const handleTokens = async () => {
    if (idToken && accessToken && refreshToken && connectionState === 'connected-awaiting-login') {
      //console.log("Handling tokens", refreshToken)
      const tokensExpired = checkIfTokensExpired(accessToken, idToken, refreshToken)
      if (tokensExpired) {
        setLoggedIn(false)
        console.log("Tokens expired, refreshing...")
        const newTokens = await fetchRefreshedTokens(refreshToken)
        if (newTokens && newTokens.id_token && newTokens.access_token) {
          console.log("🎱 New tokens received")
          setIdToken(newTokens.id_token)
          setAccessToken(newTokens.access_token)
          localStorage.setItem('localIdToken', newTokens.id_token)
          localStorage.setItem('localAccessToken', newTokens.access_token)
          sendTokensToWebsocket(newTokens.access_token, newTokens.id_token, newTokens.refresh_token)
        } else {
          console.log("Error: Failed to refresh tokens")
          console.log(newTokens)
          handleLogout()
        }
      } else {
        //console.log("Tokens are still valid")
        sendTokensToWebsocket(accessToken, idToken, refreshToken)
      }
    }
  }


  const getUserStats = () => {
    console.log('📊 User Stats:')
    const userStats: ObjectStringKeyAnyValue = {}
    userStats['deviceDetails'] = navigator.userAgent
    userStats['language'] = navigator.language
    return userStats
  }

  const sendTokensToWebsocket = async (accessToken: string, idToken: string, refreshToken: string) => {
    //console.log("Sending tokens to websocket")
    const payloadObj: ObjectStringKeyAnyValue = {
      action: "login",
      loggingInFromApp: "portal",
      idToken: idToken,
      accessToken: accessToken,
      userStats: getUserStats()
    }
    const selectedPortalId = localStorage.getItem("selectedPortalId")
    if (selectedPortalId) {
      payloadObj['selectedPortalId'] = selectedPortalId
    }
    const payload = JSON.stringify(payloadObj)
    sendMessageToWebsocket(payload)
  }

  const checkIfTokensExpired = (accessToken: string, idToken: string, refreshToken: string) => {
    const tokenExpiry = new Date(JSON.parse(atob(idToken.split('.')[1])).exp * 1000)
    const currentTime = new Date()
    const timeUntilExpiry = tokenExpiry.getTime() - currentTime.getTime()
    const refreshIfRemainingTimeIsLessThan = 5 * 60 * 1000 // 5 minutes in milliseconds
    //console.log(`Time until expiry: ${timeUntilExpiry / 60000}mins. Time limit: ${refreshIfRemainingTimeIsLessThan / 60000}mins`)
    if (timeUntilExpiry < refreshIfRemainingTimeIsLessThan) {
      return true
    }
    else {
      return false
    }
  }


  const fetchRefreshedTokens = async (refreshToken: string) => {
    //console.log("Fetching refreshed tokens for token: ", refreshToken)
    const clientId = CloudformationOutputs.UserPoolClientId
    const userPoolDomain = CloudformationOutputs.UserPoolDomain
    const awsRegion = CloudformationOutputs.AWSRegion
    const url = `https://${userPoolDomain}.auth.${awsRegion}.amazoncognito.com/oauth2/token`

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken
    })

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(`Error refreshing token: ${error.error_description}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to refresh token:', error)
      return null
    }
  }



  const checkForToken = () => {
    //console.log('checking for a token')
    const localIdToken = localStorage.getItem("localIdToken")
    const localAccessToken = localStorage.getItem("localAccessToken")
    const localRefreshToken = localStorage.getItem("localRefreshToken")
    const queryStringParams = window.location.hash.replace("#", "").split(/[&=]+/)

    let idToken = null
    let accessToken = null
    let refreshToken = null

    const indexOfIdToken = queryStringParams.indexOf('id_token')
    if (indexOfIdToken >= 0) {
      //console.log(`id token found at index ${indexOfIdToken}`)
      idToken = queryStringParams[indexOfIdToken + 1]
      //console.log(`🎱 id token found: ${idToken}`)
    } else {
      //console.log(`id token not found in query string`)
    }

    const indexOfAccessToken = queryStringParams.indexOf('access_token')
    if (indexOfAccessToken >= 0) {
      //console.log(`access token found at index ${indexOfAccessToken}`)
      accessToken = queryStringParams[indexOfAccessToken + 1]
      //console.log(`🥎 access token found: ${accessToken}`)
    }


    const indexOfRefreshToken = queryStringParams.indexOf('refresh_token')
    if (indexOfRefreshToken >= 0) {
      refreshToken = queryStringParams[indexOfRefreshToken + 1]
      //console.log(`🏀 refresh token found: ${refreshToken}`)
    }


    if (idToken && accessToken && refreshToken) {
      console.log('login pending')
      setLoggedIn('pending')
      setIdToken(idToken)
      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
      localStorage.setItem('localIdToken', idToken)
      localStorage.setItem('localAccessToken', accessToken)
      localStorage.setItem('localRefreshToken', refreshToken)
      window.location.href = '/'

    } else if (localIdToken && localAccessToken && localRefreshToken) {
      //console.log('logged in earlier')
      setIdToken(localIdToken)
      setAccessToken(localAccessToken)
      setRefreshToken(localRefreshToken)
    } else {
      //console.log('not logged in')
      //window.location.href = loginUrl
    }
  }

  const switchCurrentPortalId = (portalId: string) => {
    //console.log(`Portal id change requested to ${portalId}`)
    localStorage.setItem("selectedPortalId", portalId)
    setCurrentPortalId(portalId)
    setTableData(null)
    setConnectionState('closed')
    socket?.close()
    window.location.href = '/'

    // if (idToken && accessToken && socket) {
    //   const payloadObj: { [index: string]: any } = {
    //     action: "login",
    //     switchPortal: true,
    //     loggingInFromApp: "portal",
    //     idToken: idToken || '',
    //     accessToken: accessToken || '',
    //     selectedPortalId: portalId
    //   }
    //   console.log(`sending switch portal id request to websocket:`)
    //   console.log(payloadObj)
    //   const payload = JSON.stringify(payloadObj)
    //   sendMessageToWebsocket(payload)
    // }
    // window.location.href = '/'
  }

  const sendMessageToWebsocket = async (payload: string) => {
    if (socketRef.current) {
      // console.log(`sending to websocket: ${JSON.parse(payload).action}`)
      // console.log(`sending to websocket: ${payload}`)
      socketRef.current.send(payload)
    } else {
      console.log('🚫  Can not send to web socket')
    }
  }


  function checkPaymentDetailsAndRedirect(redirectUrl: string) {
    const lastPaymentTimestamp = localStorage.getItem('lastPaymentRequestTimestamp');
    const currentTime = Date.now();

    // Check if the timestamp exists and is less than 2 minutes ago
    if (lastPaymentTimestamp) {
      const timeElapsed = currentTime - parseInt(lastPaymentTimestamp, 10);

      // If less than 10 minutes have passed, do nothing
      if (timeElapsed < 2 * 60 * 1000) {
        console.log('Payment details were requested less than 2 minutes ago. No redirect.');
        return;
      }
    }
    // If the timestamp doesn't exist or more than 2 minutes have passed, update the timestamp and redirect
    window.location.href = redirectUrl;
  }

  const mergeTableData = (newData: ObjectStringKeyAnyValue) => {
    const oldTableData = tableDataRef.current
    if (oldTableData) {
      let mergedTableData = { ...oldTableData };
      Object.keys(newData).forEach(key => {
        // Directly assigning the value from newData, overriding the oldData value if key exists in both
        mergedTableData[key] = newData[key];
      });
      setTableData(mergedTableData)
      tableDataRef.current = mergedTableData
    } else {
      setTableData(newData)
      tableDataRef.current = newData
    }
  }


  function mergeObjects(oldData: any, newData: any): any {
    if (Array.isArray(oldData) || Array.isArray(newData)) {
      return newData; // Preserve arrays by replacing old with new
    }

    if (typeof oldData !== 'object' || oldData === null) return newData;
    if (typeof newData !== 'object' || newData === null) return oldData;

    let result = { ...oldData };

    for (let key in newData) {
      if (newData.hasOwnProperty(key)) {
        result[key] = mergeObjects(oldData[key], newData[key]);
      }
    }

    return result;
  }


  const handleLogin = (loginDataBody: ObjectStringKeyAnyValue) => {
    const loggedInPortalId = loginDataBody.selectedPortalId
    // if (!loggedInPortalId) {
    //   setShowModal({ "error": `User does not have access to any portal accounts` })
    //   handleLogout()
    // }
    // else {
    setShowModal(null)
    setLoggedIn(true)
    setConnectionState('connected-and-logged-in')
    if (loginDataBody && Object.keys(loginDataBody).length > 0) {
      loginDataBody.idTokenDecoded && setUserData(loginDataBody.idTokenDecoded.payload)
      setOrgSettings(loginDataBody.settings)
      setActiveFeatures(loginDataBody['activeFeatures'] || {})
      setExpiredFeatures(loginDataBody['expiredFeatures'] || {})
    }
    if (loggedInPortalId) {
      setCurrentPortalId(loggedInPortalId)
      localStorage.setItem('selectedPortalId', loggedInPortalId)
    }
    let requestAssets = JSON.stringify({
      action: "portal",
      subAction: "getPortalData",
      selectedPortalId: loggedInPortalId
    })
    //console.log(`Requesting portal data for portal id: ${loggedInPortalId}`)
    sendMessageToWebsocket(requestAssets)
    // }
  }

  const actOnMessage = () => {
    if (socket && socket.onmessage === null) {
      socket.onmessage = ({ data }: any) => {
        if (data) { 
          const { action, payload } = JSON.parse(data)
          publish(action, payload)
          //console.log(`📨 A message has been received: ${action }`)
          switch (action) {
            case 'error':
              console.log(`🚫  Error: ${JSON.stringify(payload)}`)
              if (payload && payload.message) {
                setShowModal({ "error": ` ${payload.message}` })
              }
              break

            case 'autherror':
              console.log('🚫  Authentication error - logging you out')
              handleLogout()
              break

            case 'loggedIn':
              setShowLoginModal(false)
              //console.log("🪵 logged in")
              setConnectionState('connected-and-logged-in')
              handleLogin(JSON.parse(data).body)
              break


            case 'loginFailed':
              handleLogout()
              break

            case 'loginOKButNoOrganisation':
              window.localStorage.setItem('loggedInNoOrgId', data)
              console.log("🚫  Login OK but no organisation")
              if (JSON.parse(data) && JSON.parse(data).idTokenDecoded) {
                setUserData(JSON.parse(data).idTokenDecoded.payload)
              }
              // localStorage.removeItem('localIdToken')
              // localStorage.removeItem('localAccessToken')
              handleLogin(JSON.parse(data).body)
              break

            case 'returnMultiTableData':
              //console.log(`❎ Multiple tables received`)
              // console.log(`❎ Multiple tables received: ${JSON.stringify(Object.keys(payload))}`)
              mergeTableData(payload)
              break

            case 'mergeSingleTableData':
              const newData = mergeObjects(tableDataRef.current, payload)
              setTableData(newData)
              tableDataRef.current = newData
              break

            case 'checkPeriodSummaryForOrg':
              const organisationId = payload['organisationId']
              const checkPeriodSummary = payload['checkPeriodSummary']
              const newTableDataWithCheckPeriods: ObjectStringKeyAnyValue = { ...tableDataRef.current }
              if (
                newTableDataWithCheckPeriods['Organisations'] &&
                newTableDataWithCheckPeriods['Organisations'][organisationId]) {
                newTableDataWithCheckPeriods['Organisations'][organisationId]['CheckPeriodSummary'] = checkPeriodSummary
              }
              setTableData(newTableDataWithCheckPeriods)
              tableDataRef.current = newTableDataWithCheckPeriods
              break

            case 'mergeSingleItemData':
              const resultTypeKeyToMerge = Object.keys(payload)[0]
              const resultToMerge = payload[resultTypeKeyToMerge]
              const resultKeyToMerge = Object.keys(resultToMerge)[0]
              const resultValueToMerge = resultToMerge[resultKeyToMerge]

              //console.log(`❎  updated single result received for table: ${resultTypeKeyToMerge} ${resultKeyToMerge}`)
              // if the item already exists, merge it
              const newTableDataObjToMerge: ObjectStringKeyAnyValue = { ...tableDataRef.current }
              newTableDataObjToMerge[resultTypeKeyToMerge] ||= {}
              if (newTableDataObjToMerge[resultTypeKeyToMerge][resultKeyToMerge]) {
                newTableDataObjToMerge[resultTypeKeyToMerge][resultKeyToMerge] = mergeObjects(newTableDataObjToMerge[resultTypeKeyToMerge][resultKeyToMerge], resultValueToMerge)
              } else {
                newTableDataObjToMerge[resultTypeKeyToMerge][resultKeyToMerge] = resultValueToMerge
              }
              setTableData(newTableDataObjToMerge)
              tableDataRef.current = newTableDataObjToMerge

              break

            case 'returnSingleItem':
              const resultTypeKey = Object.keys(payload)[0]
              const result = payload[resultTypeKey]
              const resultKey = Object.keys(result)[0]
              const resultValue = result[resultKey]
              //console.log(`❎  updated single result received for table: ${resultTypeKey} ${resultKey}. Value: ${JSON.stringify(resultValue)}`)
              const newTableDataObj: ObjectStringKeyAnyValue = { ...tableDataRef.current }
              newTableDataObj[resultTypeKey] ||= {}
              newTableDataObj[resultTypeKey][resultKey] = resultValue

              setTableData(newTableDataObj)
              tableDataRef.current = newTableDataObj
              break

            case 'returnSingleTableData':
              const newKey = Object.keys(payload)[0]
              const newValue = payload[newKey]
              const newTableData = { ...tableDataRef.current }
              newTableData[newKey] = newValue
              setTableData(newTableData)
              tableDataRef.current = newTableData
              //console.log(`❎  updated table received: ${Object.keys(payload)[0]} at time: ${Date.now()}`)
              break

            case 'tableUpdateItemAdded':
            case 'tableUpdateItemModified':
              const tableToUpdate = payload['tableName']
              const itemToUpdate = payload['keys'] ? payload['keys'][Object.keys(payload['keys'])[0]] : null
              const newValues = payload['newImage'] || payload['newRecord']
              const updatedTableData = { ...tableDataRef.current }
              updatedTableData[tableToUpdate] ||= {}
              if (itemToUpdate) {
                updatedTableData[tableToUpdate][itemToUpdate] = newValues
              } else {
                updatedTableData[tableToUpdate] = newValues
              }
              setTableData(updatedTableData)
              tableDataRef.current = updatedTableData
              break
            case 'tableUpdateItemRemoved':
              //console.log(`❎  item removed!!`)
              const tableToRemoveItemFrom = Object.keys(payload)[0]
              const itemToRemoveKey: any = Object.values(payload)[0]
              //console.log(`❎  item removed from table: ${tableToRemoveItemFrom} with key: ${itemToRemoveKey}`)
              const tableDataWithItemRemoved = { ...tableDataRef.current }
              if (tableDataWithItemRemoved[tableToRemoveItemFrom] && tableDataWithItemRemoved[tableToRemoveItemFrom][itemToRemoveKey]) {
                delete tableDataWithItemRemoved[tableToRemoveItemFrom][itemToRemoveKey]
              }
              setTableData(tableDataWithItemRemoved)
              tableDataRef.current = tableDataWithItemRemoved
              break

            case 'ok':
              //console.log('super admin command ok')
              break

            case 'agreementNeeded':
              console.log("📝 Electronic agreement needed")
              console.log(payload)
              setAgreementNeeded(payload)
              break;
            case 'agreementSaved':
              console.log("📝 Electronic agreement saved")
              console.log(payload)
              setAgreementNeeded(null)
              break;
            case 'addUserResponse':
            case 'deleteUserResponse':
            case 'switchUserRoleResponse':
              // console.log(`❎  user table update: ${JSON.stringify(payload)}`)
              // console.log(Object.keys(payload))
              if (payload && Object.keys(payload).indexOf('error') === -1) {
                // console.log("❎  user table update recieved:")
                // console.log(payload)
                mergeTableData({ 'Users': payload })
                // console.log("❎  user table updated to")
                // console.log(tableData && tableData['Users'])
              }
              break
            case 'paymentMethodNeeded':
              checkPaymentDetailsAndRedirect(payload['portalUrl'])
              break;


            default:
              //console.log(`📧 A message has been received for action: ${action}`)
              break
          }
        }
      }
    }
  }

  const handleLogout = () => {
    setLoggedIn(false)
    setUserData(null)
    setAccessToken(null)
    setIdToken(null)
    setRefreshToken(null)
    localStorage.removeItem('localIdToken')
    localStorage.removeItem('localAccessToken')
    localStorage.removeItem('localRefreshToken')
    // close the websocket
    if (socket) {
      socket.close()
    }
  }

  if (!loggedIn) {
    return <GlobalContext.Provider value={{
      tableData,
      userData,
      currentPortalId,
      switchCurrentPortalId,
      sendMessageToWebsocket,
      activeFeatures,
      expiredFeatures,
      loggedIn,
      orgSettings,
      setShowModal,
      connectionState,
      showLoginModal,
      setShowLoginModal,
      getUserStats,
      socket
    }}><Router>
        {showLoginModal && <LoginModal />}
        {showModal && <MessageModal showModal={showModal} />}
        <Routes>
          <Route path='/*' element={<NotLoggedIn showForm={'login'} />} />
          <Route path='/forgotPassword' element={<NotLoggedIn showForm={'forgotPassword'} />} />
        </Routes>
      </Router>
    </GlobalContext.Provider>
  }



  return <div className={`h-full flex flex-col min-h-screen justify-between`}>

    <div className={`h-full bg-gray-200 flex flex-col flex-grow items-center`}>
      <GlobalContext.Provider value={{
        tableData,
        userData,
        currentPortalId,
        switchCurrentPortalId,
        sendMessageToWebsocket,
        activeFeatures,
        expiredFeatures,
        loggedIn,
        orgSettings,
        setShowModal,
        connectionState
      }}>

        <Router>

          {showReconnectModal && <ReconnectModal />}
          {showModal && <MessageModal showModal={showModal} />}
          <Header handleLogout={handleLogout} />

          <div className={`max-w-3xl w-full p-5 flex flex-col gap-5 items-start justify-center`}>

            {/* <p>Current Portal ID:{currentPortalId}</p> */}

            {/* <JsonDebug jsonobject={tableData} /> */}


            {agreementNeeded && <AppTermsDialog details={agreementNeeded} />}

            {!currentPortalId && <NoOrganisation handleLogout={handleLogout} />}

            {!tableData && currentPortalId && <div className={`w-full mt-6 flex flex-col justify-center`}><Spinner size='large'>Loading...</Spinner></div>}

            {tableData && currentPortalId && <Routes>

              <Route path='/*' element={<Dashboard />} />
              <Route path='/users' element={<ListUsers />} />

              <Route
                path='/automatic-reports/'
                element={<AutomaticReportsList />} />
              <Route
                path='/automatic-report/'
                element={<AutomaticReportsForm />} />
              <Route
                path='/automatic-report/:Id'
                element={<AutomaticReportsForm />} />


            </Routes>}



          </div>
        </Router>
      </GlobalContext.Provider>

    </div>
    <Footer />
  </div>

}

export default App
